import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { getAluminiumStock } from './stockActions';
import { KTIcon, enumSystemPolicyType } from '../../../../_metronic/helpers';
import { SystemPolicyChecker } from '../../../../_metronic/helpers/components/SystemPolicyChecker';
import MiniScoreGraph from './MiniScoreGraph';

const AluminiumStockCard = ({ aluminiumStock, getAluminiumStock }) => {
    const intl = useIntl();
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [filter, setFilter] = useState('30D');

    useEffect(() => {
        getAluminiumStock();
    }, [getAluminiumStock]);

    const { totalStockWeight, totalAvailableStockWeight, prices, unitPriceDate, history } = aluminiumStock || {};

    const currentPrice = (prices || []).find(p => p.currency === selectedCurrency) || {
        unitPrice: aluminiumStock?.unitPrice || 0,
        totalPrice: aluminiumStock?.totalStockPrice || 0,
        currency: aluminiumStock?.currency || 'USD'
    };

    const baseCurrency = aluminiumStock?.currency || 'USD';
    const basePriceObj = (prices || []).find(p => p.currency === baseCurrency);
    const basePrice = basePriceObj ? basePriceObj.unitPrice : (aluminiumStock?.unitPrice || 1);
    const exchangeRate = basePrice !== 0 ? (currentPrice.unitPrice / basePrice) : 1;

    if (!aluminiumStock || !history || history.length === 0) {
        return (
            <div className='card mb-5 mb-xl-8'>
                <div className='card-body d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
                    <span className="spinner-border text-primary" role="status"></span>
                    <span className="text-gray-800 fs-6 fw-semibold ms-3">{intl.formatMessage({ id: 'GENERAL.LOADING' }, { defaultMessage: 'Loading...' })}</span>
                </div>
            </div>
        );
    }

    const filteredHistory = history.slice(- (filter === '7D' ? 7 : filter === '30D' ? 30 : 90));
    
    const weightHistory = filteredHistory.map(h => ({ date: h.date, value: h.weight }));
    const availableWeightHistory = filteredHistory.map(h => ({ date: h.date, value: h.availableWeight }));
    const priceHistory = filteredHistory.map(h => ({ date: h.date, value: h.unitPrice * exchangeRate }));

    return (
        <div className="card mb-5 mb-xl-8">
            <div className="card-header border-0 pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold fs-3 mb-1">
                        {intl.formatMessage({ id: 'STOCK.ALUMINIUM.TITLE' }, { defaultMessage: 'Aluminium Stock' })}
                    </span>
                </h3>
                <div className="card-toolbar">
                    <div className="d-flex align-items-center">
                        <SystemPolicyChecker policyId={enumSystemPolicyType.MetalPrice}>
                            <select
                                className="form-select form-select-sm form-select-solid w-80px me-3"
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                            >
                                <option value="EUR">EUR</option>
                                <option value="USD">USD</option>
                                <option value="TRY">TRY</option>
                            </select>
                        </SystemPolicyChecker>
                        <div className="btn-group">
                            {['7D', '30D', '3M'].map((f) => (
                                <button
                                    key={f}
                                    className={`btn btn-sm btn-${filter === f ? 'primary' : 'light'} btn-active-primary px-4`}
                                    onClick={() => setFilter(f)}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body p-9">
                <div className="row g-5 g-xl-8">
                    <div className="col-md-6 col-lg-3">
                        <div className="card h-100 border border-gray-300 border-dashed rounded p-4">
                            <div className="d-flex align-items-center mb-3">
                                <div className="symbol symbol-40px me-3">
                                    <span className="symbol-label bg-light-success">
                                        <KTIcon iconName='cube-2' className='fs-2 text-success' />
                                    </span>
                                </div>
                                <div className="d-flex flex-column">
                                    <span className="fs-4 text-gray-800 fw-bolder">
                                        {totalStockWeight ? intl.formatNumber(totalStockWeight, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0} kg
                                    </span>
                                    <span className="fs-7 text-muted fw-bold">{intl.formatMessage({ id: 'STOCK.FIELD.TOTAL_WEIGHT' }, { defaultMessage: 'Total Weight' })}</span>
                                </div>
                            </div>
                            <div className="separator separator-dashed mb-3"></div>
                            <div style={{ height: '50px' }}>
                                <MiniScoreGraph 
                                    data={weightHistory} 
                                    color="#50cd89" 
                                    height={50} 
                                    formatter={(val) => (val / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' t'}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6 col-lg-3">
                        <div className="card h-100 border border-gray-300 border-dashed rounded p-4">
                            <div className="d-flex align-items-center mb-3">
                                <div className="symbol symbol-40px me-3">
                                    <span className="symbol-label" style={{ backgroundColor: 'rgba(0, 158, 247, 0.15)', color: '#009ef7' }}>
                                        <KTIcon iconName='cube-3' className='fs-2' />
                                    </span>
                                </div>
                                <div className="d-flex flex-column">
                                    <span className="fs-4 text-gray-800 fw-bolder">
                                        {totalAvailableStockWeight ? intl.formatNumber(totalAvailableStockWeight, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0} kg
                                    </span>
                                    <span className="fs-7 text-muted fw-bold">{intl.formatMessage({ id: 'STOCK.FIELD.AVAILABLE_WEIGHT' }, { defaultMessage: 'Available Weight' })}</span>
                                </div>
                            </div>
                            <div className="separator separator-dashed mb-3"></div>
                            <div style={{ height: '50px' }}>
                                <MiniScoreGraph 
                                    data={availableWeightHistory} 
                                    color="#009ef7" 
                                    height={50} 
                                    formatter={(val) => (val / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' t'}
                                />
                            </div>
                        </div>
                    </div>

                    <SystemPolicyChecker policyId={enumSystemPolicyType.MetalPrice}>
                        <div className="col-md-6 col-lg-3">
                            <div className="card h-100 border border-gray-300 border-dashed rounded p-4">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="symbol symbol-40px me-3">
                                        <span className="symbol-label bg-light-warning">
                                            <KTIcon iconName='dollar' className='fs-2 text-warning' />
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column">
                                        <span className="fs-4 text-gray-800 fw-bolder">
                                            {intl.formatNumber(currentPrice.unitPrice, { style: 'currency', currency: selectedCurrency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: 4 })} / kg
                                        </span>
                                        <span className="fs-7 text-muted fw-bold">{intl.formatMessage({ id: 'STOCK.FIELD.UNIT_PRICE' }, { defaultMessage: 'Unit Price' })}</span>
                                    </div>
                                </div>
                                <div className="separator separator-dashed mb-3"></div>
                                <div style={{ height: '50px' }}>
                                    <MiniScoreGraph 
                                        data={priceHistory} 
                                        color="#ffc700" 
                                        height={50} 
                                        formatter={(val) => intl.formatNumber(val, { style: 'currency', currency: selectedCurrency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: 4 })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6 col-lg-3">
                            <div className="card h-100 border border-gray-300 border-dashed rounded p-4">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="symbol symbol-40px me-3">
                                        <span className="symbol-label bg-light-info">
                                            <KTIcon iconName='financial-schedule' className='fs-2 text-info' />
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column">
                                        <span className="fs-4 text-gray-800 fw-bolder">
                                            {intl.formatNumber(currentPrice.totalPrice, { style: 'currency', currency: selectedCurrency, currencyDisplay: 'narrowSymbol' })}
                                        </span>
                                        <span className="fs-7 text-muted fw-bold">{intl.formatMessage({ id: 'STOCK.FIELD.TOTAL_PRICE' }, { defaultMessage: 'Total Price' })}</span>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center justify-content-center h-50px">
                                    <span className="fs-6 text-gray-600 fw-bold">
                                        {intl.formatMessage({ id: 'SETTING.COMPANY_ALUMINIUM_WORKMANSHIP_PRICE' })}: {intl.formatNumber((aluminiumStock.workmanshipPrice || 0), { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol' })} / kg
                                    </span>
                                </div>
                            </div>
                        </div>
                    </SystemPolicyChecker>
                </div>

            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        aluminiumStock: state.Stock.aluminiumStock
    };
};

const mapActionsToProps = {
    getAluminiumStock
};

export default connect(mapStateToProps, mapActionsToProps)(AluminiumStockCard);
